import {BaseComponent} from './base-component.js';
import {customElement, property, query} from 'lit/decorators.js';
import { html, css, CSSResultGroup, TemplateResult, svg } from 'lit'
import uid from './utils/uniq-id'

@customElement('il-checkbox')
export default class CheckboxComponent extends BaseComponent {
  @property({type: Boolean}) checked?: boolean;
  @property({type: Boolean}) indeterminate?: boolean;
  @property({type: String}) label?: string | number;
  @property({type: Boolean}) disabled?: boolean;
  @property({type: Boolean, attribute: 'align-to-top'}) alignToTop?: boolean;
  @property({type: String, attribute: 'error-message'}) errorMessage?: string;

  @property({type: Object}) onChange?: (currentValue: boolean) => void;

  @query('slot[name="hint"]') private hintSlot?: HTMLSlotElement;

  /**
   * The hint wrapper is inline-block, so leaving it in the flow with nothing slotted still
   * generates a line box and pads dead space under the label.
   */
  get shouldRenderHint(): boolean {
    return Boolean(
      this.hintSlot
        ?.assignedNodes({flatten: true})
        .some((node) => node.nodeType !== Node.TEXT_NODE || node.textContent?.trim())
    );
  }

  _onHintSlotChange(): void {
    this.requestUpdate();
  }

  static override styles: CSSResultGroup = [
    BaseComponent.styles,
    css`
      .il-checkbox-container {
        cursor: pointer;
      }

      .il-checkbox-wrapper {
        position: relative;
      }

      .il-checkbox__clickable-container {
        position: relative;
        display: inline-flex;
      }

      .il-checkbox-container--disabled .il-checkbox__checkmark,
      .il-checkbox-container--disabled .il-checkbox__hint,
      .il-checkbox-container--disabled .il-checkbox__error-message {
        opacity: 0.5;
      }

      .il-checkbox-container--disabled .il-checkbox__label-container {
        opacity: var(--il-checkbox-disabled-label-opacity, 0.5);
      }

      .il-checkbox-container.il-checkbox-container--align-to-top .il-checkbox-wrapper {
        align-items: initial;
      }

      .il-checkbox__label-container {
        padding-top: 4px;
      }

      .il-checkbox__input {
        box-sizing: border-box;
        cursor: pointer;
        font-family: inherit;
        font-size: 100%;
        line-height: 1.15;
        margin: 0;
        opacity: 0.0001;
        overflow: visible;
        padding: 0;
        position: absolute;
        width: 100%;
        height: 100%;
        z-index: 1;
      }

      .il-checkbox__checkmark {
        align-items: center;
        border-color: var(--il-color-neutrals-200);
        border-style: solid;
        border-width: 1px;
        border-radius: 6px;
        display: flex;
        flex-shrink: 0;
        height: 28px;
        justify-content: center;
        margin-right: 8px;
        width: 28px;
      }

      .il-checkbox__checkmark svg {
        display: none;
        height: 28px; // this is necessary so it does not break the UI alignment
      }

      .il-checkbox-container:hover:not(.il-checkbox-container--disabled)
      .il-checkbox__input
      + .il-checkbox__checkmark {
        border-color: var(--il-color-neutrals-250);
      }

      .il-checkbox-container:hover:not(.il-checkbox-container--disabled)
      .il-checkbox__input.il-checkbox__input--checked
      + .il-checkbox__checkmark,
      .il-checkbox-container:hover:not(.il-checkbox-container--disabled)
      .il-checkbox__input.il-checkbox__input--indeterminate
      + .il-checkbox__checkmark {
        border-color: var(--il-color-accent-400);
        color: var(--il-color-accent-400);
      }

      .il-checkbox-container:active-visible
      .il-checkbox__input.il-checkbox__input--checked
      + .il-checkbox__checkmark,
      .il-checkbox-container:active-visible
      .il-checkbox__input.il-checkbox__input--indeterminate
      + .il-checkbox__checkmark {
        border-color: var(--il-color-accent-200);
        color: var(--il-color-accent-400);
      }

      .il-checkbox__input.il-checkbox__input--checked + .il-checkbox__checkmark,
      .il-checkbox__input.il-checkbox__input--indeterminate
      + .il-checkbox__checkmark {
        border-color: var(--il-color-accent-300);
        color: var(--il-color-accent-300);
      }

      .il-checkbox__input:focus-visible + .il-checkbox__checkmark {
        border-color: var(--il-color-accent-200);
      }

      .il-checkbox__input.il-checkbox__input--checked
      + .il-checkbox__checkmark
      svg,
      .il-checkbox__input.il-checkbox__input--indeterminate
      + .il-checkbox__checkmark
      svg {
        display: inline;
      }

      .il-checkbox-container .il-checkbox__error-message {
        display: flex;
        align-items: flex-start;
        gap: var(--il-size-8);

        color: var(--il-color-warning);
        font-size: var(--il-font-size-14);
        line-height: var(--il-font-line-height-140);

        padding-left: 32px;
      }

      .il-checkbox-container .il-checkbox__label {
        color: var(--il-color-neutrals-300);
        display: inline;
        font-size: var(--il-font-size-14);
        line-height: var(--il-font-line-height-150);
      }

      .il-checkbox-container .il-checkbox__hint {
        color: var(--il-color-neutrals-300);
        display: inline-block;
        font-size: var(--il-font-size-12);
        line-height: var(--il-font-line-height-150);
        letter-spacing: var(--il-font-letter-spacing-1);
        padding-left: 36px;
      }

      .il-checkbox-container .il-checkbox__hint--empty {
        display: none;
      }
    `,
  ] as CSSResultGroup;

  /**
   * returns an internal id to be used on the input and label
   * gets the @id property passed from the glimmer component and adds so it is a unique ID
   *
   * if this component is used without glimmer, this should also work by passing the @id property
   * when calling the web component HTML tag, e.g.:
   * <iliad-checkbox id="some-id"></iliad-cheboxk>
   */
  get internalId(): string {
    const id = this.id ? this.id : uid('iliad-checkbox');
    return `iliad-${id}`;
  }

  override render(): TemplateResult {
    return html`
      <div
        class="
          ${this.baseCSSClass}
          il-checkbox-container
          ${this.alignToTop ? 'il-checkbox-container--align-to-top' : ''}
          ${this.disabled ? 'il-checkbox-container--disabled' : ''}

        "
        data-test-iliad-checkbox-container
      >
        <div class="il-checkbox-wrapper">
          <div class="il-checkbox__clickable-container">
            <input
              id=${this.internalId}
              type="checkbox"
              class="
            il-checkbox__input
            ${this.checked ? 'il-checkbox__input--checked' : ''}
            ${this.indeterminate ? 'il-checkbox__input--indeterminate' : ''}
          "
              ?checked=${this.checked}
              ?disabled=${this.disabled}
              @change=${this._onChange}
              data-test-iliad-checkbox
            />
            <span class="il-checkbox__checkmark">${this.getCheckIcon()}</span>

            <div class="il-checkbox__label-container">
              <slot name="label-before" data-test-iliad-checkbox-label-before></slot>

              <label
                for=${this.internalId}
                class="il-checkbox__label"
                data-test-iliad-checkbox-label
              >
                <slot name="label">${this.label}</slot>
              </label>

              <slot name="label-after" data-test-iliad-checkbox-label-after></slot>
            </div>
          </div>

          <div
            class="
              il-checkbox__hint
              ${this.shouldRenderHint ? '' : 'il-checkbox__hint--empty'}
            "
            data-test-iliad-checkbox-hint
          >
            <slot name="hint" @slotchange=${this._onHintSlotChange}></slot>
          </div>
        </div>

        ${this.renderErrorMessage()}
      </div>
    `;
  }

  private renderErrorMessage(): TemplateResult {
    if (this.errorMessage) {
      return html`<div
          class="il-checkbox__error-message"
          data-test-iliad-checkbox-error-message
        >
          ${this.alertIcon}
          ${this.errorMessage}
        </div>`;
      }

      return html``;
    }

  private getCheckIcon(): TemplateResult {
    if (this.checked) {
      return html`
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
        >
          <path
            d="M8.13273 15.3229L3.20801 10.3982L4.51996 9.08621L8.17608 12.7423L15.9394 4.979L17.208 6.2476L8.13273 15.3229Z"
            fill="currentColor"
          />
        </svg>
      `;
    }

    if (this.indeterminate) {
      return html`
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M4.16699 10.833V9.08301H15.833V10.833H4.16699Z"
            fill="currentColor"
          />
        </svg>
      `;
    }

    return html``;
  }

  private _onChange(e: Event): void {
    const currentValue = (e.target as HTMLInputElement).checked;
    if (this.onChange) {
      this.onChange(currentValue);
    } else {
      this.checked = currentValue;
    }
  }

  private alertIcon = svg`<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
  <g id="Icon20/Alert">
  <path id="Vector" d="M9.12499 10.792H10.875V5.85399H9.12499V10.792ZM9.99999 14.188C10.236 14.188 10.441 14.108 10.615 13.948C10.7883 13.788 10.875 13.576 10.875 13.312C10.875 13.076 10.7883 12.8713 10.615 12.698C10.441 12.5247 10.236 12.438 9.99999 12.438C9.76399 12.438 9.55899 12.5247 9.38499 12.698C9.21166 12.8713 9.12499 13.076 9.12499 13.312C9.12499 13.576 9.21166 13.788 9.38499 13.948C9.55899 14.108 9.76399 14.188 9.99999 14.188ZM9.99999 18.333C8.84733 18.333 7.76399 18.1143 6.74999 17.677C5.73599 17.2397 4.85399 16.646 4.10399 15.896C3.35399 15.146 2.76033 14.264 2.32299 13.25C1.88566 12.236 1.66699 11.1527 1.66699 9.99999C1.66699 8.84733 1.88566 7.76399 2.32299 6.74999C2.76033 5.73599 3.35399 4.85399 4.10399 4.10399C4.85399 3.35399 5.73599 2.76033 6.74999 2.32299C7.76399 1.88566 8.84733 1.66699 9.99999 1.66699C11.1527 1.66699 12.236 1.88566 13.25 2.32299C14.264 2.76033 15.146 3.35399 15.896 4.10399C16.646 4.85399 17.2397 5.73599 17.677 6.74999C18.1143 7.76399 18.333 8.84733 18.333 9.99999C18.333 11.1527 18.1143 12.236 17.677 13.25C17.2397 14.264 16.646 15.146 15.896 15.896C15.146 16.646 14.264 17.2397 13.25 17.677C12.236 18.1143 11.1527 18.333 9.99999 18.333ZM9.99999 16.583C11.8193 16.583 13.3713 15.9407 14.656 14.656C15.9407 13.3713 16.583 11.8193 16.583 9.99999C16.583 8.18066 15.9407 6.62866 14.656 5.34399C13.3713 4.05933 11.8193 3.41699 9.99999 3.41699C8.18066 3.41699 6.62866 4.05933 5.34399 5.34399C4.05933 6.62866 3.41699 8.18066 3.41699 9.99999C3.41699 11.8193 4.05933 13.3713 5.34399 14.656C6.62866 15.9407 8.18066 16.583 9.99999 16.583Z" fill="currentColor"/>
  </g>
  </svg>
  `;

  /**
   * This will run when a property on the component is changed
   * We need this so we can:
   * - set the `indeterminate` to the checkbox using JS at the first render, since only adding a property to the input does not change
   *   checked to indeterminate
   * - remove the `indeterminate` from the input and the component when the user clicks on the checkbox. The only values the checkbox can have through cliking
   *   is true or false. `indeterminate` is can only be set using JS.
   */
  override updated(changedProperties: Map<string, unknown>) {
    if (
      changedProperties.has('indeterminate') ||
      changedProperties.has('checked')
    ) {
      const checkboxElement = this.shadowRoot?.querySelector('input');

      if (checkboxElement) {
        if (this.indeterminate && this.checked) {
          // Reset indeterminate when checked changes to true
          this.indeterminate = false;
          checkboxElement.indeterminate = false;
        } else if (this.indeterminate && !this.checked) {
          checkboxElement.indeterminate = true;
          checkboxElement.checked = false;
        }
      }
    }
  }
}
